import React, { DragEvent, useMemo } from "react";
import { useFileUpload } from "./useFileUpload";
import { FileUploadInfo } from "./FileUploadInfo";
import { ValidationErrors } from "./ValidationErrors";
import { DropZone } from "./DropZone";
import { FileList } from "./FileList";
import { UploadButton } from "./UploadButton";
import { UploadProgress } from "./UploadProgress";
import AttachmentField from "./compact/AttachmentField";
import { UploadUserInfo } from "./types";
import { DEFAULT_CONFIG } from "./utils";
import { IAttachment } from "../approval/approval-form/approval-form";

interface FileUploadWithAxiosProps {
  mode?: "full" | "compact";
  uuid: string;
  onFilesChange?: (files: FileList | null) => void;
  setUserInfo: (userInfo: UploadUserInfo) => void;
  attachments?: IAttachment[];
  disabled?: boolean;
}

const FileUploadWithAxios: React.FC<FileUploadWithAxiosProps> = ({
  mode = "full",
  uuid,
  onFilesChange,
  setUserInfo,
  attachments,
  disabled = false
}) => {
  const {
    selectedFiles,
    uploadProgress,
    isUploading,
    isDragOver,
    validationErrors,
    addFiles,
    removeFile,
    setDragOver,
    uploadFiles,
    config
  } = useFileUpload(DEFAULT_CONFIG, setUserInfo, uuid);

  // 컴팩트 모드 (결재 양식용)
  if (mode === "compact") {
    return (
      <AttachmentField
        config={config}
        onFilesChange={onFilesChange}
        disabled={disabled}
        setUserInfo={setUserInfo}
        uuid={uuid}
        attachments={attachments}
      />
    );
  }

  // 기존 풀 모드
  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragOver(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      addFiles(files);
    }
  };

  const handleFileSelect = (files: FileList): void => {
    addFiles(files);
  };

  const hasFiles = selectedFiles && selectedFiles.length > 0;
  const hasErrors = validationErrors.length > 0;

  return (
    <div style={{ padding: "20px", maxWidth: "700px", margin: "0 auto" }}>
      <h3>다중 파일 업로드</h3>

      <FileUploadInfo config={config} />

      <ValidationErrors errors={validationErrors} />

      <DropZone
        isDragOver={isDragOver}
        isUploading={isUploading}
        config={config}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onFileSelect={handleFileSelect}
      />

      {hasFiles && (
        <FileList
          files={selectedFiles}
          isUploading={isUploading}
          config={config}
          onRemoveFile={removeFile}
        />
      )}

      <UploadButton
        isUploading={isUploading}
        hasFiles={!!hasFiles}
        hasErrors={hasErrors}
        fileCount={selectedFiles?.length || 0}
        onUpload={uploadFiles}
      />

      <UploadProgress progress={uploadProgress} />
    </div>
  );
};

export default FileUploadWithAxios;
